/**
 * Minimal Kakao JS SDK loader for link sharing (AC-5).
 *
 * The SDK is loaded lazily on first share. If VITE_KAKAO_JS_KEY is unset or the
 * script fails to load, `loadKakao()` resolves false and the caller falls back
 * to copying the link (graceful degradation — never block sharing).
 */
declare global {
  interface Window {
    Kakao?: {
      isInitialized: () => boolean;
      init: (key: string) => void;
      Share?: {
        sendDefault: (settings: unknown) => void;
      };
    };
  }
}

// 공식 CDN 은 t1.kakaocdn.net 이다. t1.kakao.com 은 DNS 가 없어 로드가 항상 실패했다.
const SDK_SRC = 'https://t1.kakaocdn.net/kakao_js_sdk/2.7.2/kakao.min.js';
const SDK_INTEGRITY =
  'sha384-TiCUE00h649CAMonG018J2ujOgDKW/kVWlChEuu4jK2vxfAAD0eZxzCKakxg55G4';

let loadPromise: Promise<boolean> | null = null;

export function loadKakao(): Promise<boolean> {
  const key = import.meta.env.VITE_KAKAO_JS_KEY;
  if (!key) return Promise.resolve(false);
  if (typeof window === 'undefined') return Promise.resolve(false);

  if (window.Kakao?.isInitialized()) return Promise.resolve(true);

  if (!loadPromise) {
    loadPromise = new Promise<boolean>((resolve) => {
      const script = document.createElement('script');
      script.src = SDK_SRC;
      script.integrity = SDK_INTEGRITY;
      script.crossOrigin = 'anonymous';
      script.async = true;
      script.onload = () => {
        try {
          if (window.Kakao && !window.Kakao.isInitialized()) {
            window.Kakao.init(key);
          }
          resolve(Boolean(window.Kakao?.isInitialized()));
        } catch {
          resolve(false);
        }
      };
      script.onerror = () => resolve(false);
      document.head.appendChild(script);
    });
  }
  return loadPromise;
}

export function shareToKakao({
  title,
  description,
  url,
}: {
  title: string;
  description: string;
  url: string;
}): void {
  if (!window.Kakao?.Share) return;
  window.Kakao.Share.sendDefault({
    objectType: 'feed',
    content: {
      title,
      description,
      // No image asset in V1; Kakao falls back to the OG image / no image.
      link: { mobileWebUrl: url, webUrl: url },
    },
    buttons: [
      {
        title: '보러 가기',
        link: { mobileWebUrl: url, webUrl: url },
      },
    ],
  });
}
